import { useRef } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { isEmbedded, useAuthStore, useDesignStore } from '@/stores';
import { LoadingState } from '@/components/ui';
import { ImpersonationEndedScreen } from '@/components/impersonation/ImpersonationEndedScreen';
import {
  isImpersonationTab,
  readImpersonation,
} from '@/lib/impersonation-session';
import { IframeNoticeScreen } from './IframeNoticeScreen';
import { useSessionCheck } from './useSessionCheck';

interface ProtectedRouteProps {
  /** Wrapped element; without it the guard acts as a layout route. */
  children?: React.ReactNode;
}

/**
 * Protected Route - redirects to login if not authenticated.
 *
 * Used two ways: as a layout element (`<Route element={<ProtectedRoute />}>`)
 * rendering an `Outlet` for the seller branch, and as a wrapper with
 * `children` for the Creator routes.
 *
 * Inside a shop's iframe there is no login page, so a missing or expired
 * session never redirects there. The seller branch shows the notice in
 * place of the page; the Creator keeps its canvas mounted and shows the
 * notice as an overlay, so nothing the seller drew disappears with the
 * session.
 */
export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const location = useLocation();
  const { t } = useTranslation();
  const { isAuthenticated, isLoading, isIframe, sessionExpired } =
    useAuthStore();
  const hasUnsavedChanges = useDesignStore((state) => state.hasUnsavedChanges);
  const isChecking = useSessionCheck();

  // Whether this guard has rendered its children with a live session. Only
  // then is there a canvas worth keeping under the overlay; a fresh mount
  // after the expiry gets the plain notice.
  const hadSessionRef = useRef(false);
  if (isAuthenticated) {
    hadSessionRef.current = true;
  }

  if (isLoading || isChecking) {
    return <LoadingState fullscreen label={t('common.loading')} />;
  }

  // Subscribed to `isIframe` above, so a flag change re-evaluates this.
  const embedded = isIframe && isEmbedded();

  if (embedded) {
    if (children !== undefined && sessionExpired && hadSessionRef.current) {
      return (
        <>
          {children}
          <IframeNoticeScreen
            reason="sessionExpired"
            unsavedChanges={hasUnsavedChanges}
            overlay
          />
        </>
      );
    }
    if (!isAuthenticated || sessionExpired) {
      return <IframeNoticeScreen reason="sessionExpired" />;
    }
  } else if (!isAuthenticated) {
    // An impersonation tab must never reach /login, where someone could sign
    // in inside a tab kept apart from the admin's session.
    if (isImpersonationTab()) {
      return (
        <ImpersonationEndedScreen
          reason={readImpersonation()?.endedReason ?? 'revoked'}
        />
      );
    }
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children !== undefined ? <>{children}</> : <Outlet />;
}
